import { CachedData, CacheMetadata, UserSessionStore } from './types';

export class CacheManager {
  private dbName = 'LGCCache';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private isSupported = true;
  private isCorrupted = false;
  private memoryCache = new Map<string, any>(); // Fallback memory cache
  
  // Cache management settings
  private maxCacheSize = 50 * 1024 * 1024; // 50MB default
  private maxCacheAge = 7 * 24 * 60 * 60 * 1000; // 7 days default
  private cleanupInterval = 24 * 60 * 60 * 1000; // 24 hours
  private lastCleanup = 0;
  private maxMemoryCacheSize = 100; // Max items in memory cache

  constructor() {
    this.initDB();
  }

  // Error recovery methods
  private async handleCorruption(): Promise<void> {
    console.warn('Cache corruption detected, attempting recovery');
    this.isCorrupted = true;
    
    try {
      // Try to delete the corrupted database
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      
      await new Promise<void>((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase(this.dbName);
        deleteRequest.onsuccess = () => {
          console.log('Corrupted cache database deleted');
          resolve();
        };
        deleteRequest.onerror = () => reject(deleteRequest.error);
        deleteRequest.onblocked = () => {
          console.warn('Cache database deletion blocked');
          resolve(); // Continue anyway
        };
      });
      
      // Try to reinitialize
      await this.initDB();
      this.isCorrupted = false;
      console.log('Cache database successfully recovered');
    } catch (error) {
      console.error('Failed to recover from cache corruption:', error);
      this.fallbackToMemoryCache();
    }
  }

  private fallbackToMemoryCache(): void {
    console.warn('Falling back to memory-only caching');
    this.isSupported = false;
    this.isCorrupted = false;
    this.db = null;
    this.memoryCache.clear();
  }

  private addToMemoryCache<T>(key: string, data: T, metadata: CacheMetadata): void {
    // Implement LRU for memory cache
    if (this.memoryCache.size >= this.maxMemoryCacheSize) {
      const firstKey = this.memoryCache.keys().next().value;
      if (firstKey) {
        this.memoryCache.delete(firstKey);
      }
    }
    
    this.memoryCache.set(key, { data, metadata });
  }

  private getFromMemoryCache<T>(key: string): CachedData<T> | null {
    return this.memoryCache.get(key) || null;
  }

  private isCorruptionError(error: any): boolean {
    if (!error) return false;
    
    const errorMessage = error.message || error.name || '';
    const corruptionIndicators = [
      'database disk image is malformed',
      'database corruption',
      'UnknownError',
      'InvalidStateError'
    ];
    
    return corruptionIndicators.some(indicator => 
      errorMessage.toLowerCase().includes(indicator.toLowerCase())
    );
  }

  // Configure cache settings
  configure(options: {
    maxCacheSize?: number;
    maxCacheAge?: number;
    cleanupInterval?: number;
  }): void {
    if (options.maxCacheSize !== undefined) {
      this.maxCacheSize = options.maxCacheSize;
    }
    if (options.maxCacheAge !== undefined) {
      this.maxCacheAge = options.maxCacheAge;
    }
    if (options.cleanupInterval !== undefined) {
      this.cleanupInterval = options.cleanupInterval;
    }
    
    console.log('Cache configuration updated:', {
      maxCacheSize: this.maxCacheSize,
      maxCacheAge: this.maxCacheAge,
      cleanupInterval: this.cleanupInterval
    });
  }

  private async initDB(): Promise<void> {
    if (typeof window === 'undefined' || !window.indexedDB) {
      console.warn('IndexedDB not supported, falling back to memory-only caching');
      this.isSupported = false;
      return;
    }

    try {
      this.db = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(this.dbName, this.dbVersion);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          
          // Create cache store
          if (!db.objectStoreNames.contains('cache')) {
            const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
            cacheStore.createIndex('userId', 'metadata.userId', { unique: false });
            cacheStore.createIndex('timestamp', 'metadata.timestamp', { unique: false });
          }
          
          // Create user session store
          if (!db.objectStoreNames.contains('userSessions')) {
            db.createObjectStore('userSessions', { keyPath: 'userId' });
          }
        };
      });
    } catch (error) {
      console.warn('Failed to initialize IndexedDB, falling back to memory-only caching:', error);
      this.isSupported = false;
    }
  }

  async get<T>(key: string): Promise<CachedData<T> | null> {
    // Try memory cache first if IndexedDB is not available
    if (!this.isSupported) {
      return this.getFromMemoryCache<T>(key);
    }

    if (!this.db) {
      return null;
    }

    try {
      return await new Promise<CachedData<T> | null>((resolve, reject) => {
        const transaction = this.db!.transaction(['cache'], 'readonly');
        const store = transaction.objectStore('cache');
        const request = store.get(key);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result;
          if (result) {
            resolve({
              data: result.data,
              metadata: result.metadata
            });
          } else {
            resolve(null);
          }
        };
      });
    } catch (error) {
      console.warn('Cache get failed:', error);
      
      // Check if this looks like corruption
      if (this.isCorruptionError(error)) {
        await this.handleCorruption();
      }
      
      return null;
    }
  }

  async set<T>(key: string, data: T, metadata: CacheMetadata): Promise<void> {
    // Use memory cache if IndexedDB is not available
    if (!this.isSupported) {
      this.addToMemoryCache(key, data, metadata);
      return;
    }

    if (!this.db) {
      return;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['cache'], 'readwrite');
        const store = transaction.objectStore('cache');
        const request = store.put({
          key,
          data,
          metadata
        });
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
      
      // Update user session cache keys
      await this.updateUserSessionCacheKeys(metadata.userId, key);
      
      // Trigger maintenance if needed (non-blocking)
      this.performMaintenance().catch(error => {
        console.warn('Cache maintenance failed:', error);
      });
    } catch (error) {
      console.warn('Cache set failed:', error);
      
      // Check if this looks like corruption
      if (this.isCorruptionError(error)) {
        await this.handleCorruption();
        // Try to save to memory cache as fallback
        this.addToMemoryCache(key, data, metadata);
      }
    }
  }

  async clear(pattern?: string): Promise<void> {
    // Clear memory cache
    if (!this.isSupported) {
      if (pattern) {
        for (const key of this.memoryCache.keys()) {
          if (key.includes(pattern)) {
            this.memoryCache.delete(key);
          }
        }
      } else {
        this.memoryCache.clear();
      }
      return;
    }

    if (!this.db) {
      return;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['cache'], 'readwrite');
        const store = transaction.objectStore('cache');
        
        if (pattern) {
          // Clear entries matching pattern
          const request = store.openCursor();
          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result;
            if (cursor) {
              if (cursor.key.toString().includes(pattern)) {
                cursor.delete();
              }
              cursor.continue();
            } else {
              resolve();
            }
          };
          request.onerror = () => reject(request.error);
        } else {
          // Clear all entries
          const request = store.clear();
          request.onerror = () => reject(request.error);
          request.onsuccess = () => resolve();
        }
      });
    } catch (error) {
      console.warn('Cache clear failed:', error);
      
      if (this.isCorruptionError(error)) {
        await this.handleCorruption();
      }
    }
  }

  async validateUserSession(userId: string): Promise<void> {
    if (!this.isSupported || !this.db) {
      return;
    }

    try {
      // Get all cache entries for other users and remove them
      await new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['cache'], 'readwrite');
        const store = transaction.objectStore('cache');
        const index = store.index('userId');
        const request = index.openCursor();
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            const entry = cursor.value;
            if (entry.metadata.userId !== userId) {
              cursor.delete();
            }
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });
      
      // Update user session
      await this.updateUserSession(userId);
    } catch (error) {
      console.warn('User session validation failed:', error);
    }
  }

  private async updateUserSessionCacheKeys(userId: string, cacheKey: string): Promise<void> {
    if (!this.db) return;
    return new Promise<void>((resolve, reject) => {
      try {
        const transaction = this.db!.transaction(['userSessions'], 'readwrite');
        const store = transaction.objectStore('userSessions');

        // Get existing session or create new one
        const getRequest = store.get(userId);
        getRequest.onsuccess = () => {
          const session: UserSessionStore = getRequest.result || {
            userId,
            lastLogin: Date.now(),
            cacheKeys: []
          };

          // Add cache key if not already present
          if (!session.cacheKeys.includes(cacheKey)) {
            session.cacheKeys.push(cacheKey);
          }

          // Update session
          store.put(session);
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  private async updateUserSession(userId: string): Promise<void> {
    if (!this.db) return;
    return new Promise<void>((resolve, reject) => {
      try {
        const transaction = this.db!.transaction(['userSessions'], 'readwrite');
        const store = transaction.objectStore('userSessions');

        const getRequest = store.get(userId);
        getRequest.onsuccess = () => {
          const existing = getRequest.result;
          store.put({
            userId,
            lastLogin: Date.now(),
            cacheKeys: existing?.cacheKeys ?? []
          });
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  // Cleanup old cache entries (can be called periodically)
  async cleanup(maxAge: number = this.maxCacheAge): Promise<void> {
    if (!this.isSupported || !this.db) {
      return;
    }

    const cutoffTime = Date.now() - maxAge;
    
    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(['cache'], 'readwrite');
        const store = transaction.objectStore('cache');
        const index = store.index('timestamp');
        const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });
      
      this.lastCleanup = Date.now();
      console.log('Cache cleanup completed');
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }

  // Get cache statistics
  async getCacheStats(): Promise<{
    totalEntries: number;
    estimatedSize: number;
    oldestEntry: number;
    newestEntry: number;
  }> {
    if (!this.isSupported || !this.db) {
      return { totalEntries: 0, estimatedSize: 0, oldestEntry: 0, newestEntry: 0 };
    }

    try {
      return await new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(['cache'], 'readonly');
        const store = transaction.objectStore('cache');
        const request = store.getAll();
        
        request.onsuccess = () => {
          const entries = request.result;
          let estimatedSize = 0;
          let oldestEntry = Date.now();
          let newestEntry = 0;
          
          entries.forEach(entry => {
            // Rough size estimation
            estimatedSize += JSON.stringify(entry).length * 2; // UTF-16 bytes
            if (entry.metadata.timestamp < oldestEntry) {
              oldestEntry = entry.metadata.timestamp;
            }
            if (entry.metadata.timestamp > newestEntry) {
              newestEntry = entry.metadata.timestamp;
            }
          });
          
          resolve({
            totalEntries: entries.length,
            estimatedSize,
            oldestEntry: entries.length > 0 ? oldestEntry : 0,
            newestEntry: entries.length > 0 ? newestEntry : 0
          });
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn('Failed to get cache stats:', error);
      return { totalEntries: 0, estimatedSize: 0, oldestEntry: 0, newestEntry: 0 };
    }
  }

  // LRU eviction when cache size exceeds limit
  async evictLRUEntries(targetSize: number = this.maxCacheSize * 0.8): Promise<void> {
    if (!this.isSupported || !this.db) {
      return;
    }

    try {
      const stats = await this.getCacheStats();
      if (stats.estimatedSize <= targetSize) {
        return; // No eviction needed
      }

      console.log(`Cache size (${stats.estimatedSize} bytes) exceeds limit, starting LRU eviction`);

      // Get all entries sorted by timestamp (oldest first)
      const entries = await new Promise<any[]>((resolve, reject) => {
        const transaction = this.db!.transaction(['cache'], 'readonly');
        const store = transaction.objectStore('cache');
        const index = store.index('timestamp');
        const request = index.getAll();
        
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // Calculate how much to remove
      let currentSize = stats.estimatedSize;
      const entriesToRemove: string[] = [];

      for (const entry of entries) {
        if (currentSize <= targetSize) {
          break;
        }
        
        const entrySize = JSON.stringify(entry).length * 2;
        currentSize -= entrySize;
        entriesToRemove.push(entry.key);
      }

      // Remove oldest entries
      if (entriesToRemove.length > 0) {
        await new Promise<void>((resolve, reject) => {
          const transaction = this.db!.transaction(['cache'], 'readwrite');
          const store = transaction.objectStore('cache');
          
          let completed = 0;
          entriesToRemove.forEach(key => {
            const request = store.delete(key);
            request.onsuccess = () => {
              completed++;
              if (completed === entriesToRemove.length) {
                resolve();
              }
            };
            request.onerror = () => reject(request.error);
          });
        });

        console.log(`Evicted ${entriesToRemove.length} cache entries via LRU`);
      }
    } catch (error) {
      console.warn('LRU eviction failed:', error);
    }
  }

  // Automatic maintenance (call periodically)
  async performMaintenance(): Promise<void> {
    const now = Date.now();
    
    // Only run maintenance if enough time has passed
    if (now - this.lastCleanup < this.cleanupInterval) {
      return;
    }

    console.log('Starting cache maintenance');
    
    // Clean up old entries
    await this.cleanup();
    
    // Check size and evict if necessary
    const stats = await this.getCacheStats();
    if (stats.estimatedSize > this.maxCacheSize) {
      await this.evictLRUEntries();
    }
    
    console.log('Cache maintenance completed', await this.getCacheStats());
  }

  // Get cache health status
  getCacheHealth(): {
    isSupported: boolean;
    isCorrupted: boolean;
    isUsingMemoryFallback: boolean;
    memoryCacheSize: number;
  } {
    return {
      isSupported: this.isSupported,
      isCorrupted: this.isCorrupted,
      isUsingMemoryFallback: !this.isSupported,
      memoryCacheSize: this.memoryCache.size
    };
  }

  // Force recovery attempt
  async forceRecovery(): Promise<boolean> {
    try {
      await this.handleCorruption();
      return !this.isCorrupted && this.isSupported;
    } catch (error) {
      console.error('Force recovery failed:', error);
      return false;
    }
  }
}

// Singleton instance
export const cacheManager = new CacheManager();